import { useCallback, useMemo, useState } from 'react';

import { toast } from 'react-toastify';

import { getBaseOpenApiSpec } from 'services/api/ApiAccessService';
import { downloadFile, mapSchemaForDownload } from '../apiUtils';

export default function useApiDownloads({ baseId, schemaDoc }) {
  const [isDownloadingOpenApi, setIsDownloadingOpenApi] = useState(false);

  const basePlaceholder = baseId || '<base-id>';

  const handleDownloadSchema = useCallback(() => {
    if (!schemaDoc) return;

    const mappedSchemaDoc = mapSchemaForDownload(schemaDoc);

    downloadFile(
      `base-${basePlaceholder}-schema.json`,
      JSON.stringify(mappedSchemaDoc, null, 2),
      'application/json;charset=utf-8',
    );
  }, [basePlaceholder, schemaDoc]);

  const handleDownloadOpenApi = useCallback(async () => {
    if (!baseId || isDownloadingOpenApi) return;

    setIsDownloadingOpenApi(true);

    try {
      const payload = await getBaseOpenApiSpec({ baseId });
      downloadFile(
        `base-${basePlaceholder}-openapi.json`,
        JSON.stringify(payload || {}, null, 2),
        'application/json;charset=utf-8',
      );
      toast.success('OpenAPI JSON downloaded.');
    } catch (error) {
      toast.error(error?.message || 'Failed to download OpenAPI JSON.');
    } finally {
      setIsDownloadingOpenApi(false);
    }
  }, [baseId, basePlaceholder, isDownloadingOpenApi]);

  return useMemo(() => ({
    canDownloadOpenApi: Boolean(baseId) && !isDownloadingOpenApi,
    canDownloadSchema: Boolean(schemaDoc),
    handleDownloadOpenApi,
    handleDownloadSchema,
  }), [
    baseId,
    handleDownloadOpenApi,
    handleDownloadSchema,
    isDownloadingOpenApi,
    schemaDoc,
  ]);
}
